import { useState } from "react";
import { useLocation } from "react-router-dom";
import { ChatCircleText, X, Bug, Lightbulb, Question, DotsThree, PaperPlaneTilt, CheckCircle, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const FEEDBACK_TYPES = [
  { key: "bug", label: "Bug", icon: Bug, color: "#DC2626" },
  { key: "feature", label: "Feature", icon: Lightbulb, color: "#FF9800" },
  { key: "question", label: "Question", icon: Question, color: "#0EA5E9" },
  { key: "other", label: "Other", icon: DotsThree, color: "#71717A" },
];

const PUBLIC_PATH_PREFIXES = [
  "/login",
  "/reset-password",
  "/signup",
  "/privacy",
  "/terms",
  "/contact",
  "/faq",
];

export default function FeedbackWidget({ variant = "floating", className = "" }) {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState("feature");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  if (!user) return null;
  // Floating launcher hides on public/marketing routes. Inline trigger is
  // explicitly placed by the consumer so we trust them.
  if (variant === "floating") {
    if (location.pathname === "/") return null;
    if (PUBLIC_PATH_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))) {
      return null;
    }
  }

  const reset = () => {
    setFeedbackType("feature");
    setSubject("");
    setMessage("");
    setSent(false);
  };

  const close = () => {
    setOpen(false);
    setTimeout(reset, 250);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 4) {
      toast.error("Message must be at least 4 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        feedback_type: feedbackType,
        subject: subject.trim(),
        message: message.trim(),
      });
      setSent(true);
      setTimeout(close, 2200);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Launcher button — floating bottom-right OR inline (caller-positioned) */}
      {variant === "floating" ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="feedback-widget-button"
          aria-label="Send feedback"
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 bg-[#1A2332] hover:bg-[#0F172A] text-white shadow-2xl border-2 border-[#FF9800] px-4 py-3 font-bold text-[10px] uppercase tracking-widest transition-all active:scale-95"
        >
          <ChatCircleText size={16} weight="fill" className="text-[#FF9800]" />
          <span className="hidden sm:inline">Feedback</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="feedback-widget-button"
          aria-label="Send feedback"
          className={`inline-flex items-center gap-2 bg-[#1A2332] hover:bg-[#FF9800] hover:text-[#1A2332] text-white border-2 border-[#1A2332] px-4 py-2.5 font-bold text-[10px] sm:text-xs uppercase tracking-widest transition-colors ${className}`}
        >
          <ChatCircleText size={16} weight="fill" className="text-[#FF9800]" />
          Send feedback
        </button>
      )}

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[55] bg-[#0F172A]/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
          onClick={close}
          data-testid="feedback-modal-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white border-2 border-[#1A2332] w-full sm:max-w-lg sm:my-8 max-h-[92vh] overflow-y-auto"
            data-testid="feedback-modal"
          >
            <div className="bg-[#1A2332] text-white px-5 py-4 flex items-center justify-between gap-3 sticky top-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <ChatCircleText size={18} weight="fill" className="text-[#FF9800] flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF9800]">
                    {sent ? "Thanks!" : "Send feedback"}
                  </div>
                  <div className="font-display text-base font-bold leading-tight truncate">
                    {sent ? "We received your message" : "Help us improve ezItemizer"}
                  </div>
                </div>
              </div>
              <button
                onClick={close}
                data-testid="feedback-modal-close"
                aria-label="Close"
                className="p-1.5 hover:bg-white/10 flex-shrink-0"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            {sent ? (
              <div className="px-6 py-12 text-center" data-testid="feedback-success">
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#FFF1E0] mb-4">
                  <CheckCircle size={36} weight="fill" className="text-[#FF9800]" />
                </div>
                <div className="font-display text-xl font-bold text-[#0F172A] mb-1.5">
                  Got it — thank you!
                </div>
                <div className="text-sm text-zinc-600 leading-relaxed max-w-sm mx-auto">
                  We've forwarded your {feedbackType} to our team. If we have follow-up questions, we'll reply to {user.email}.
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="px-5 sm:px-6 py-5 space-y-5">
                {/* Type selector */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-2 block">
                    Type
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {FEEDBACK_TYPES.map((t) => {
                      const Icon = t.icon;
                      const active = feedbackType === t.key;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setFeedbackType(t.key)}
                          data-testid={`feedback-type-${t.key}`}
                          className={`flex flex-col items-center gap-1.5 px-2 py-3 border-2 transition-colors ${
                            active
                              ? "border-[#1A2332] bg-[#1A2332] text-white"
                              : "border-zinc-200 bg-white hover:border-zinc-400 text-zinc-700"
                          }`}
                        >
                          <Icon size={20} weight={active ? "fill" : "regular"} style={{ color: active ? t.color : undefined }} />
                          <span className="text-[10px] font-bold uppercase tracking-widest">
                            {t.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-2 block">
                    Subject <span className="text-zinc-400 font-normal normal-case">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={200}
                    placeholder="Brief summary…"
                    data-testid="feedback-subject-input"
                    className="w-full border-2 border-zinc-200 focus:border-[#1A2332] focus:outline-none px-3 py-2.5 text-sm font-mono"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-2 block">
                    Message
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={5000}
                    rows={5}
                    required
                    placeholder={
                      feedbackType === "bug"
                        ? "What did you expect? What happened instead? Steps to reproduce…"
                        : feedbackType === "feature"
                        ? "What would you like to see? Why would it help?"
                        : "Tell us more…"
                    }
                    data-testid="feedback-message-input"
                    className="w-full border-2 border-zinc-200 focus:border-[#1A2332] focus:outline-none px-3 py-2.5 text-sm leading-relaxed resize-none"
                  />
                  <div className="text-[10px] text-zinc-400 font-mono mt-1 text-right">
                    {message.length}/5000
                  </div>
                </div>

                {/* Submitter context */}
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-100 px-3 py-2 font-mono leading-relaxed">
                  Sending as <span className="text-[#0F172A]">{user.name || user.email}</span> · {user.email}
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={close}
                    data-testid="feedback-cancel-button"
                    className="px-4 py-2.5 text-xs font-bold uppercase tracking-widest border border-zinc-300 hover:bg-zinc-100 text-zinc-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || message.trim().length < 4}
                    data-testid="feedback-submit-button"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF9800] hover:bg-[#E68900] disabled:opacity-50 disabled:cursor-not-allowed text-[#1A2332] font-bold text-xs uppercase tracking-widest px-4 py-3 transition-colors"
                  >
                    {submitting ? (
                      <>
                        <Spinner size={14} weight="bold" className="animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <PaperPlaneTilt size={14} weight="fill" />
                        Send feedback
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
